import React, { useState } from 'react';
import { Plus, Trash2, AlertCircle, Users, Fingerprint, CheckCircle, LogOut } from 'lucide-react';
import { useGetUsersQuery, useCreateUserMutation, useUpdateUserMutation, useDeleteUserMutation } from '../services/userApi';
import { webAuthnApi } from '../services/webauthn';
import { authApi } from '../services/api';
import UserDetailsDialog from '../components/users/UserDetailsDialog';
import type { User } from '../types/user';
import { usePermissions } from '../hooks/usePermissions';
import { useSelector } from 'react-redux';
import type { RootState } from '../store/store';
import { logAuditEvent, AUDIT_ACTIONS } from '../utils/auditLogger';

interface UserFormData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  isDisabled: boolean;
  isLockedOut: boolean;
  role: 'admin' | 'viewer';
}

const initialFormState: UserFormData = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  isDisabled: false,
  isLockedOut: false,
  role: 'viewer'
};

export default function UsersPage() {
  const { canManageUsers } = usePermissions();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [editingUser, setEditingUser] = useState<{ id: number | null; data: Partial<UserFormData> }>({
    id: null,
    data: {}
  });
  const [formData, setFormData] = useState<UserFormData>(initialFormState);
  const [formError, setFormError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [registeredBiometricIds, setRegisteredBiometricIds] = useState<Set<number>>(new Set());
  const [biometricSuccessUser, setBiometricSuccessUser] = useState<User | null>(null);
  const [userOverrides, setUserOverrides] = useState<Record<number, Partial<User>>>({});

  const { data: users, isLoading, error } = useGetUsersQuery();
  const [createUser] = useCreateUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const [deleteUser] = useDeleteUserMutation();

  const handleBiometricsRegistration = async (userId: number) => {
    try {
      const user = users?.find(u => u.id === userId);
      if (!user) {
        throw new Error('User not found');
      }

      if (!window.PublicKeyCredential) {
        alert('Your browser does not support biometric authentication');
        return;
      }

      const platformAvailable = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      if (!platformAvailable) {
        alert('Face ID, Touch ID or a device fingerprint sensor is not available on this device.\n\nPlease check:\n• Settings › Face ID & Passcode (or Touch ID & Passcode)\n• Ensure biometric authentication is enabled\n• On iPad, use Safari rather than another browser');
        return;
      }

      const companyName = localStorage.getItem('lastCompanyName');
      if (!companyName) {
        throw new Error('Company name not found');
      }

      const options = await webAuthnApi.getRegistrationOptions({
        companyName,
        email: user.email
      });

      const credential = await navigator.credentials.create({
        publicKey: options
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('Failed to create credentials');
      }

      // Prepare credential for verification
      const preparedCredential = webAuthnApi.prepareCredentialForVerify(credential);

      // Verify registration
      await webAuthnApi.verifyRegistration({
        companyName,
        email: user.email
      }, preparedCredential);

      setRegisteredBiometricIds(prev => new Set(prev).add(userId));
      setBiometricSuccessUser(user);
    } catch (error: any) {
      console.error('Biometrics registration failed:', error);
      if (error.name === 'NotAllowedError') {
        alert('Biometric setup was cancelled or timed out. Please try again and follow the on-screen prompt.');
      } else if (error.name === 'NotSupportedError') {
        alert('This device or browser does not support biometric authentication.\n\nOn iPad, please use Safari and ensure Face ID or Touch ID is configured in Settings.');
      } else if (error.name === 'SecurityError') {
        alert('Security error during biometric setup. Please ensure you are on a secure (HTTPS) connection.');
      } else if (error.name === 'InvalidStateError') {
        alert('A biometric credential already exists for this account on this device.');
      } else {
        alert(`Failed to set up biometrics: ${error.message || 'Unknown error'}. Please try again.`);
      }
    }
  };

  const handleBiometricSuccessLogout = async () => {
    setBiometricSuccessUser(null);
    try {
      await authApi.logout();
    } catch {
      // logout clears local state even on error
    }
  };

  const filteredUsers = React.useMemo(() => {
    if (!users) return [];
    const visibleUsers = canManageUsers
      ? users
      : users.filter(u => u.id === currentUser?.id);
    const base = searchQuery.trim()
      ? visibleUsers.filter(user =>
          user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : visibleUsers;
    return base.map(u => userOverrides[u.id] ? { ...u, ...userOverrides[u.id] } : u);
  }, [users, searchQuery, userOverrides, canManageUsers, currentUser?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    try {
      const roles = formData.role === 'admin' ? [1, 2] : [2];
      await createUser({
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        isDisabled: formData.isDisabled,
        isLockedOut: formData.isLockedOut,
        email: formData.email,
        password: formData.password,
        roles
      }).unwrap();
      
      // Log user creation
      await logAuditEvent(
        AUDIT_ACTIONS.USER_CREATE,
        `User created - Email: ${formData.email}, Name: ${formData.firstName} ${formData.lastName}, Role: ${formData.role}`,
        currentUser
      );
      
      setFormData(initialFormState);
      setIsAddingUser(false);
    } catch (error) {
      setFormError('Failed to create user. Please try again.');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser.id) return;
    setFormError(null);

    try {
      const roles = editingUser.data.role === 'admin' ? [1, 2] : [2];
      const updateData: UpdateUserRequest = {
        userId: editingUser.id,
        ...editingUser.data,
        roles
      };
      await updateUser(updateData).unwrap();
      
      // Log user update
      await logAuditEvent(
        AUDIT_ACTIONS.USER_EDIT,
        `User updated - User ID: ${editingUser.id}, Email: ${editingUser.data.email}, Role: ${editingUser.data.role}`,
        currentUser
      );
      
      setEditingUser({ id: null, data: {} });
    } catch (error) {
      setFormError('Failed to update user. Please try again.');
    }
  };

  const handleDelete = async (userId: number) => {
    if (!canManageUsers) return;
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const userToDelete = users?.find(u => u.id === userId);
        await deleteUser(userId).unwrap();
        
        // Log user deletion
        await logAuditEvent(
          AUDIT_ACTIONS.USER_DELETE,
          `User deleted - User ID: ${userId}, Email: ${userToDelete?.email || 'unknown'}, Name: ${userToDelete?.firstName} ${userToDelete?.lastName || 'unknown'}`,
          currentUser
        );
      } catch (error) {
        setFormError('Failed to delete user. Please try again.');
      }
    }
  };

  const handleUserClick = (user: User) => {
    if (!canManageUsers && user.id !== currentUser?.id) return;
    setSelectedUser(user);
    setIsDetailsOpen(true);
  };

  const handleSaveDetails = async (userData: Partial<User>) => {
    if (!selectedUser) return false;
    if (!canManageUsers && selectedUser.id !== currentUser?.id) return false;

    try {
      let passwordUpdated = false;
      let userDataUpdated = false;

      // Check if password is being updated
      if (userData.password) {
        const companyName = localStorage.getItem('lastCompanyName');
        if (!companyName) {
          throw new Error('Company name not found');
        }
        
        if (!canManageUsers && selectedUser.id !== currentUser?.id) {
          throw new Error('Unauthorized to change other users\' passwords');
        }

        const response = await authApi.updatePassword(
          companyName,
          selectedUser.email,
          userData.currentPassword,
          userData.password
        );
        passwordUpdated = response.message === 'Password updated successfully';
      }

      // Check if user data has changed
      const hasUserDataChanged = (
        userData.email !== selectedUser.email ||
        userData.firstName !== selectedUser.firstName ||
        userData.lastName !== selectedUser.lastName ||
        userData.phoneNumber !== selectedUser.phoneNumber ||
        userData.isDisabled !== selectedUser.isDisabled ||
        userData.isLockedOut !== selectedUser.isLockedOut ||
        (userData.role && userData.role !== (selectedUser.role === 'admin' ? '1' : '2'))
      );

      if (hasUserDataChanged) {
        if (!canManageUsers) {
          delete userData.role;
        }

        const updateData: UpdateUserRequest = {
          userId: selectedUser.id,
          email: selectedUser.email,
          ...userData,
          password: undefined
        };

        await updateUser({
          ...updateData
        }).unwrap();
        userDataUpdated = true;
        // Instant UI update — apply changed fields locally before refetch arrives
        setUserOverrides(prev => ({
          ...prev,
          [selectedUser.id]: { ...prev[selectedUser.id], ...userData, password: undefined }
        }));
      }
      
      if (!passwordUpdated) {
        setIsDetailsOpen(false);
        setSelectedUser(null);
      }

      // Return true only if something was actually updated
      return passwordUpdated || userDataUpdated;
    } catch (error) {
      console.error('Update error:', error);
      setFormError('Failed to update user details. Please ensure all required fields are filled.');
      return false;
    }
  };

  if (isLoading) {
    return <div className="p-6">Loading users...</div>;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-md flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-700 dark:text-red-400">Failed to load users</span>
        </div>
      </div>
    );
  }

  const currentUserInList = users?.find(u => u.id === currentUser?.id);
  const currentUserHasBiometrics = currentUserInList?.hasBiometrics || registeredBiometricIds.has(currentUser?.id ?? -1);

  return (
    <div className="p-6">
      {/* Header section */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Users</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {canManageUsers ? 'Manage system users' : 'View system users'}
          </p>
          {canManageUsers && (
            <div className="relative mt-3 w-full max-w-md">
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-3 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md
                  focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500
                  bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-w-0"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  ×
                </button>
              )}
            </div>
          )}
        </div>

        {/* Only show Add User button for admins */}
        {canManageUsers && (
          <button
            type="button"
            onClick={() => setIsAddingUser(true)}
            className="inline-flex w-full sm:w-auto shrink-0 items-center justify-center gap-2 rounded-md bg-indigo-600 hover:bg-indigo-700 px-4 py-2.5 text-sm font-medium text-white transition-colors"
          >
            <Plus className="h-5 w-5 shrink-0" aria-hidden />
            Add User
          </button>
        )}
      </div>

      {/* Biometric login card — always visible, self-service only */}
      {currentUser && (
        <div className="mb-6 bg-app-panel dark:bg-gray-800 shadow rounded-lg p-4 flex items-center gap-4">
          <div className={`flex-shrink-0 rounded-full p-2 ${currentUserHasBiometrics ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
            <Fingerprint className={`h-7 w-7 ${currentUserHasBiometrics ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {currentUserHasBiometrics ? 'Biometric login is active on this device' : 'Set up biometric login for your account'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {currentUserHasBiometrics
                ? 'You can re-register to update your credential for this device.'
                : 'Use Face ID, Touch ID or fingerprint to log in quickly.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => currentUser.id != null && handleBiometricsRegistration(currentUser.id)}
            className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 px-3 py-2 text-sm font-medium text-white transition-colors"
          >
            <Fingerprint className="h-4 w-4" />
            {currentUserHasBiometrics ? 'Re-register' : 'Set up'}
          </button>
        </div>
      )}

      {/* Error message */}
      {formError && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 p-4 rounded-md">
          <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
        </div>
      )}

      {isAddingUser && (
        <div className="mb-6 bg-app-panel dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Add New User</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Password
                </label>
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'viewer' })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                >
                  <option value="viewer">Viewer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsAddingUser(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
              >
                Create User
              </button>
            </div>
          </form>
        </div>
      )}

      {/* User table */}
      <div className="bg-app-panel dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Name/Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                {canManageUsers && (
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-app-panel divide-y divide-gray-200 dark:bg-gray-900 dark:divide-gray-700">
              {filteredUsers.map((user) => (
                <tr 
                  key={user.id} 
                  className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                  onClick={() => handleUserClick(user)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {user.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {user.role === 'admin' ? 'Admin' : 'Viewer'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.isDisabled
                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    }`}>
                      {user.isDisabled ? 'Disabled' : 'Active'}
                    </span>
                  </td>
                  {canManageUsers && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(user.id);
                        }}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* User details dialog */}
      <UserDetailsDialog
        user={selectedUser}
        isOpen={isDetailsOpen}
        onClose={() => {
          setIsDetailsOpen(false);
          setSelectedUser(null);
        }}
        onSave={handleSaveDetails}
        canEditRoles={canManageUsers}
        currentUserId={currentUser?.id ?? null}
      />

      {/* Biometric registration success modal */}
      {biometricSuccessUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-app-panel dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="h-8 w-8 text-green-500 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Biometrics Registered</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {biometricSuccessUser.firstName} {biometricSuccessUser.lastName}
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
              Biometric login has been set up on this device. To use it, you must log out and select <strong>Login with Biometrics</strong> on the login screen.
            </p>
            {biometricSuccessUser.id === currentUser?.id ? (
              <>
                <p className="text-sm text-indigo-600 dark:text-indigo-400 mb-4">
                  This is your account — log out now to activate biometric login.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setBiometricSuccessUser(null)}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Stay logged in
                  </button>
                  <button
                    onClick={handleBiometricSuccessLogout}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
                  >
                    <LogOut className="h-4 w-4" />
                    Log out now
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  They will need to log out and back in on this device to use biometric login.
                </p>
                <button
                  onClick={() => setBiometricSuccessUser(null)}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
                >
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}